import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField } from "@mui/material";
import { errorMessage, fetchMyCorrections, submitCorrection } from "../../timesheets/corrections/correctionsApi";
import {
  canAnswer,
  clockLabel,
  clockOutProblem,
  clockOutValue,
  dayLabel,
  hhmm,
  isPlaceholderOut,
  PLACEHOLDER_MESSAGE,
} from "../../timesheets/corrections/correctionTime";

/* Attendance clock-out corrections (owner 10-19): when the system clocked someone out automatically,
   ask them at their next CARE sign-in for the real clock-out time; the answer goes to the approver
   (Dr. Gowtham). Mounted once in App.js (signed-in pages only).

   - Asked once per page load (sign-in) and again when the tab comes back on a new day. Users with
     nothing to answer (the kiosk's service login included) never see anything.
   - Never blocks work: closing the dialog (or "Remind me later") hides it for 4 hours in this tab.
   - Clock-out only (no clock-in, no added sessions), on the clock-in's day, after the clock-in, not
     in the future, not past the next clock-in that day; the server checks the same.
   - The approver also gets a one-per-session notice when corrections wait for a decision. */

const SNOOZE_MS = 4 * 60 * 60 * 1000;
const snoozeKey = (uid) => `attendanceCorrectionSnooze:${uid}`;
const noticeKey = (uid) => `attendanceCorrectionApproverNotice:${uid}`;

const readSession = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeSession = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* private mode / blocked storage: the prompt just comes back sooner */
  }
};

const AttendanceCorrectionHost = () => {
  const user = useSelector((state) => state.userconfig);
  const navigate = useNavigate();
  const uid = user?.id;
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(0);
  const lastCheckDay = useRef(null);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    if (!uid || busyRef.current) return;
    busyRef.current = true;
    lastCheckDay.current = dayjs().format("YYYY-MM-DD");
    try {
      const res = await fetchMyCorrections();
      const data = res?.data || {};
      if (!data.enabled) return;
      const actionable = (data.items || []).filter((it) => canAnswer(it));
      setItems(actionable);
      setDrafts((prev) => {
        const next = {};
        actionable.forEach((it) => {
          next[it.id] = prev[it.id] || {
            time: isPlaceholderOut(it) ? "" : hhmm(it.recorded_clock_out), note: "", error: "", busy: false, done: "",
          };
        });
        return next;
      });
      const snoozedAt = Number(readSession(snoozeKey(uid)) || 0);
      if (actionable.length && Date.now() - snoozedAt > SNOOZE_MS) setOpen(true);
      if (data.is_approver && data.approval_waiting > 0 && !readSession(noticeKey(uid))) {
        setNotice(data.approval_waiting);
      }
    } catch (e) {
      /* v2 down or not deployed yet: no prompt, nothing else changes */
    } finally {
      busyRef.current = false;
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && lastCheckDay.current !== dayjs().format("YYYY-MM-DD")) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const setDraft = (id, patch) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const send = async (item, timeValue) => {
    const draft = drafts[item.id] || {};
    const problem = isPlaceholderOut(item) && timeValue === hhmm(item.recorded_clock_out)
      ? PLACEHOLDER_MESSAGE
      : clockOutProblem(item, timeValue);
    if (problem) {
      setDraft(item.id, { error: problem });
      return;
    }
    setDraft(item.id, { busy: true, error: "" });
    try {
      const res = await submitCorrection(item.id, clockOutValue(item, timeValue), draft.note);
      const approved = res?.data?.status === "approved";
      setDraft(item.id, { busy: false, done: approved ? "Approved." : "Sent for approval." });
    } catch (err) {
      setDraft(item.id, { busy: false, error: errorMessage(err, "Not sent. Try again.") });
    }
  };

  const remindLater = () => {
    writeSession(snoozeKey(uid), String(Date.now()));
    setOpen(false);
  };

  const pending = items.filter((it) => !drafts[it.id]?.done);
  const closeDone = () => {
    setOpen(false);
    setItems(pending);
  };

  const dismissNotice = () => {
    writeSession(noticeKey(uid), "1");
    setNotice(0);
  };

  return (
    <>
      <Dialog open={open && items.length > 0} onClose={pending.length ? remindLater : closeDone} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#1D2951", fontWeight: 600 }}>Please correct your clock-out</DialogTitle>
        <DialogContent dividers>
          <p className="text-sm text-gray-600 mb-3">
            The system clocked you out automatically because no clock-out was recorded. Enter the time you
            actually left; it goes to your approver. Only the clock-out time can be changed here.
          </p>
          {items.map((item) => {
            const d = drafts[item.id] || {};
            return (
              <div key={item.id} className="border border-gray-200 rounded-lg p-3 mb-3">
                <div className="font-semibold text-[#1D2951]">{dayLabel(item.work_date)}</div>
                <div className="text-sm text-gray-700 mt-1">
                  Clocked in {clockLabel(item.clock_in)}. The system recorded your clock-out as{" "}
                  <b>{clockLabel(item.recorded_clock_out)}</b>.
                  {isPlaceholderOut(item) && " That is the automatic end of day, not a real clock-out."}
                </div>
                {item.next_clock_in && (
                  <div className="text-xs text-gray-500 mt-1">
                    Your next clock-in that day was {clockLabel(item.next_clock_in)}.
                  </div>
                )}
                {item.status === "rejected" && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    Your time ({clockLabel(item.proposed_clock_out)}) was not approved
                    {item.decision_note ? `: ${item.decision_note}` : "."} You can send one more time.
                  </Alert>
                )}
                {d.done ? (
                  <Alert severity="success" sx={{ mt: 1, py: 0 }}>{d.done}</Alert>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <label className="text-sm text-gray-700" htmlFor={`acorr-time-${item.id}`}>I clocked out at</label>
                      <input
                        id={`acorr-time-${item.id}`}
                        type="time"
                        value={d.time || ""}
                        onChange={(e) => setDraft(item.id, { time: e.target.value, error: "" })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!!d.busy}
                        onClick={() => send(item, d.time)}
                        sx={{ textTransform: "none", backgroundColor: "#1D2951" }}
                      >
                        Send for approval
                      </Button>
                      {!isPlaceholderOut(item) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!!d.busy}
                          onClick={() => send(item, hhmm(item.recorded_clock_out))}
                          sx={{ textTransform: "none" }}
                        >
                          {clockLabel(item.recorded_clock_out)} is correct
                        </Button>
                      )}
                    </div>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Note for your approver (optional)"
                      value={d.note || ""}
                      onChange={(e) => setDraft(item.id, { note: e.target.value.slice(0, 1000) })}
                      sx={{ mt: 1 }}
                    />
                    {d.error && <div className="text-sm text-red-600 mt-1">{d.error}</div>}
                    {item.respond_by && (
                      <div className="text-xs text-gray-500 mt-1">
                        Please answer by {dayjs(item.respond_by).format("ddd, MMM D h:mm A")}. After that your
                        approver sets the time.
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          {pending.length ? (
            <Button onClick={remindLater} sx={{ textTransform: "none" }}>Remind me later</Button>
          ) : (
            <Button onClick={closeDone} variant="contained" sx={{ textTransform: "none", backgroundColor: "#1D2951" }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notice > 0}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={(e, reason) => {
          if (reason !== "clickaway") dismissNotice();
        }}
      >
        <Alert
          severity="info"
          onClose={dismissNotice}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                dismissNotice();
                navigate("/timesheets?view=corrections");
              }}
            >
              Review
            </Button>
          }
        >
          {notice === 1 ? "1 attendance correction needs" : `${notice} attendance corrections need`} your decision.
        </Alert>
      </Snackbar>
    </>
  );
};

export default AttendanceCorrectionHost;
